import { RefreshCw } from 'lucide-react';
import type { ReactElement } from 'react';
import { useBcvRate } from '@/hooks/useBcvRate';

/**
 * ExchangeRateBadge — USD/VES BCV rate chip in the TopBar.
 *
 * Sprint 1 B2. Lives next to `CalculatorButton` so finance users
 * see the conversion factor without context-switching to
 * `/exchange-rates`. The component is intentionally minimal:
 *
 *   - Loading  → `—` placeholder.
 *   - Error    → returns `null` (silent hide — `/exchange-rates`
 *                remains the canonical place to check rates).
 *   - Success  → `USD → VES 36.45` (desktop) or `36.45` (mobile,
 *                ≤sm).
 *
 * Sprint 2b (kpis-de-compras WU-4): the `useBcvRate` query hook was
 * extracted to `apps/web/src/hooks/useBcvRate.ts` so the analytics
 * page currency toggle can share the same queryKey. Query behaviour
 * (staleTime, retry, etc.) is unchanged — the badge imports the
 * same hook from the new location.
 */

function formatRate(rawRate: string): string {
  const parsed = Number.parseFloat(rawRate);
  if (!Number.isFinite(parsed)) return '—';
  return parsed.toFixed(2);
}

// Re-export for unit testing without rendering.
export { formatRate };

export function ExchangeRateBadge(): ReactElement | null {
  const { data, isLoading, isError, isFetching, refetch } = useBcvRate();

  // Spec REQ-3: silent hide on error. The chip is a convenience for
  // finance users, not a critical signal — they fall back to the
  // dedicated `/exchange-rates` page if the API is down.
  if (isError) return null;

  const rateText = data ? formatRate(data.rate) : '—';

  function handleRefresh(): void {
    // `refetch()` returns a promise; we don't await it — the
    // `isFetching` flag drives the spin animation while the
    // request is in flight.
    void refetch();
  }

  return (
    <output
      className="flex items-center gap-1 rounded-md border border-border/50 bg-muted/40 px-2 py-1 font-mono text-xs text-muted-foreground"
      aria-label={`Tasa BCV USD a VES: ${rateText}`}
      data-testid="exchange-rate-badge"
    >
      <span className="hidden font-medium tracking-tight sm:inline">USD → VES</span>
      <span className="font-semibold text-foreground tabular-nums" aria-live="polite">
        {isLoading ? '—' : rateText}
      </span>
      <button
        type="button"
        onClick={handleRefresh}
        disabled={isFetching}
        aria-label="Actualizar tasa BCV ahora"
        className="ml-0.5 grid size-5 place-items-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
      >
        <RefreshCw className={`size-3 ${isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
      </button>
    </output>
  );
}
